import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Play, BarChart3, Clock, DollarSign, Users, AlertTriangle, Download, FileText } from 'lucide-react';

interface CampaignSimulationPanelProps {
  packVersionId: number;
}

interface SimulationOptions {
  operatorAvailability: 'optimistic' | 'realistic' | 'pessimistic';
}

interface SimulationResults {
  totalStores: number;
  estimatedDuration: string;
  estimatedCost: number;
  operatorRequirements: Array<{
    operatorId: number;
    operatorName: string;
    storeCount: number;
    estimatedHours: number;
    estimatedCost: number;
    availability: string;
  }>;
  timeline: Array<{
    phase: string;
    startDate: string;
    endDate: string;
    milestone: string;
    dependencies: string[];
  }>;
  risks: Array<{
    risk: string;
    probability: string;
    impact: string;
    mitigation: string;
  }>;
}

interface SimulationMetadata {
  createdAt: string;
  createdBy: number;
  simulationVersion: string;
  assumptions: string[];
  confidence: 'low' | 'medium' | 'high';
}

interface CampaignSimulation {
  packVersionId: number;
  packVersion: number;
  simulationId: string;
  results: SimulationResults;
  metadata: SimulationMetadata;
}

export const CampaignSimulationPanel: React.FC<CampaignSimulationPanelProps> = ({ 
  packVersionId 
}) => {
  const [simulation, setSimulation] = useState<CampaignSimulation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('simulation');
  const [simulationOptions, setSimulationOptions] = useState<SimulationOptions>({
    operatorAvailability: 'realistic'
  });

  const runSimulation = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/pack-versions/${packVersionId}/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({ simulationOptions })
      });

      if (response.ok) {
        const data = await response.json();
        setSimulation(data.simulation);
        setError(null);
        setActiveTab('results');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to run simulation');
      }
    } catch (err) {
      setError('Network error while running simulation');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!simulation) return;

    try {
      // Use auth-attached raw helper to fetch PDF bytes
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fetchWithSupabaseTokenRaw } = await import('@/lib/http-supa');
      try {
        const buf = await fetchWithSupabaseTokenRaw(`/api/simulations/${simulation.simulationId}/export-pdf`);
        const blob = new Blob([buf], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `simulation_${simulation.simulationId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch {
        setError('Failed to download report');
      }
    } catch (err) {
      setError('Network error while downloading report');
    }
  };

  const renderSimulationOptions = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="h-5 w-5" />
          Simulation Options
        </CardTitle>
        <CardDescription>
          Configure simulation parameters for campaign planning
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium">Operator Availability Assumption:</label>
          <Select 
            value={simulationOptions.operatorAvailability} 
            onValueChange={(value: 'optimistic' | 'realistic' | 'pessimistic') => 
              setSimulationOptions(prev => ({ ...prev, operatorAvailability: value }))
            }
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="optimistic">Optimistic - High availability</SelectItem>
              <SelectItem value="realistic">Realistic - Normal availability</SelectItem>
              <SelectItem value="pessimistic">Pessimistic - Low availability</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground mt-1">
            {simulationOptions.operatorAvailability === 'optimistic' && 
              'Assumes most operators are available and can work efficiently'
            }
            {simulationOptions.operatorAvailability === 'realistic' && 
              'Assumes normal operator availability with some constraints'
            }
            {simulationOptions.operatorAvailability === 'pessimistic' && 
              'Assumes limited operator availability and potential delays'
            }
          </p>
        </div>

        <Button 
          onClick={runSimulation} 
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Run Simulation
        </Button>
      </CardContent>
    </Card>
  );

  const renderSimulationResults = () => {
    if (!simulation) return null;

    return (
      <div className="space-y-4">
        {/* Summary Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Simulation Summary
            </CardTitle>
            <CardDescription>
              Campaign simulation results for pack version {simulation.packVersion}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 border rounded-lg">
                <Users className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold">{simulation.results.totalStores}</div>
                <div className="text-sm text-muted-foreground">Total Stores</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <Clock className="h-6 w-6 mx-auto mb-2 text-orange-600" />
                <div className="text-lg font-bold">{simulation.results.estimatedDuration}</div>
                <div className="text-sm text-muted-foreground">Duration</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <DollarSign className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold">${simulation.results.estimatedCost.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Total Cost</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <Users className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold">{simulation.results.operatorRequirements.length}</div>
                <div className="text-sm text-muted-foreground">Operators</div>
              </div>
            </div>

            {/* Confidence Level */}
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium">Simulation Confidence:</span>
                <Badge 
                  variant={
                    simulation.metadata.confidence === 'high' ? 'default' : 
                    simulation.metadata.confidence === 'medium' ? 'secondary' : 'destructive'
                  }
                >
                  {simulation.metadata.confidence.toUpperCase()}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operator Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Operator Requirements</CardTitle>
            <CardDescription>
              Detailed breakdown of operator assignments and costs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {simulation.results.operatorRequirements.map((operator) => (
                <div key={operator.operatorId} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{operator.operatorName}</div>
                    <div className="text-sm text-muted-foreground">
                      {operator.storeCount} stores • {operator.estimatedHours} hours
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">${operator.estimatedCost.toLocaleString()}</div>
                    <Badge variant="outline">{operator.availability}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Campaign Timeline</CardTitle>
            <CardDescription>
              Project phases and milestones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {simulation.results.timeline.map((phase, index) => (
                <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{phase.milestone}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(phase.startDate).toLocaleDateString()} - {new Date(phase.endDate).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge variant="outline">{phase.phase}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Risk Assessment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Risk Assessment
            </CardTitle>
            <CardDescription>
              Identified risks and mitigation strategies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {simulation.results.risks.map((risk, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{risk.risk}</div>
                    <div className="flex gap-2">
                      <Badge variant="outline">{risk.probability}</Badge>
                      <Badge variant="outline">{risk.impact}</Badge>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <strong>Mitigation:</strong> {risk.mitigation}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Assumptions */}
        <Card>
          <CardHeader>
            <CardTitle>Simulation Assumptions</CardTitle>
            <CardDescription>
              Key assumptions used in this simulation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {simulation.metadata.assumptions.map((assumption, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                  <span className="text-sm">{assumption}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={downloadReport} className="flex-1">
            <Download className="h-4 w-4 mr-2" />
            Download PDF Report
          </Button>
          <Button variant="outline" onClick={() => setActiveTab('simulation')}>
            <Play className="h-4 w-4 mr-2" />
            Run New Simulation
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="simulation">Simulation Options</TabsTrigger>
          <TabsTrigger value="results" disabled={!simulation}>Results</TabsTrigger>
        </TabsList>

        <TabsContent value="simulation">
          {renderSimulationOptions()}
        </TabsContent>

        <TabsContent value="results">
          {renderSimulationResults()}
        </TabsContent>
      </Tabs>
    </div>
  );
};
